<template>
  <section class="page-card">
    <h1 class="page-title">{{ $t('pages.recharges') }}</h1>
    <div class="filter-bar">
      <el-input v-model="search.user_id" :placeholder="$t('common.userId')" class="filter-item" size="small"></el-input>
      <el-input v-model="search.id" :placeholder="$t('common.orderId')" class="filter-item" size="small"></el-input>
      <el-select v-model="search.status" clearable :placeholder="$t('common.status')" class="filter-item" size="small">
        <el-option v-for="item in rechargeStatuses" :key="item.value" :value="item.value" :label="$t(item.labelKey)"></el-option>
      </el-select>
      <el-date-picker v-model="search.created_at__gte" type="date" value-format="yyyy-MM-dd" :placeholder="$t('common.start')" class="filter-item" size="small"></el-date-picker>
      <el-date-picker v-model="search.created_at__lt" type="date" value-format="yyyy-MM-dd" :placeholder="$t('common.end')" class="filter-item" size="small"></el-date-picker>
      <el-button type="primary" size="small" @click="reload">{{ $t('actions.search') }}</el-button>
    </div>
    <div class="summary-grid">
      <div class="metric-card">
        <div class="metric-label">{{ $t('pages.rechargeTotal') }}</div>
        <div v-if="rechargeAmounts.length" class="metric-list">
          <div v-for="item in rechargeAmounts" :key="item.currency || 'empty'" class="metric-row">
            <span>{{ item.currency || '-' }}</span>
            <strong>{{ item.amount }}</strong>
          </div>
        </div>
        <div v-else class="metric-value">0</div>
      </div>
      <div class="metric-card">
        <div class="metric-label">{{ $t('pages.successfulRechargeAmount') }}</div>
        <div v-if="successfulRechargeAmounts.length" class="metric-list">
          <div v-for="item in successfulRechargeAmounts" :key="item.currency || 'empty'" class="metric-row">
            <span>{{ item.currency || '-' }}</span>
            <strong>{{ item.amount }}</strong>
          </div>
        </div>
        <div v-else class="metric-value">0</div>
      </div>
    </div>
    <div class="table-scroll">
      <el-table :key="tableLanguage" :data="tableData" border stripe>
        <el-table-column prop="id" :label="$t('common.orderId')" min-width="150"></el-table-column>
        <el-table-column prop="third_no" :label="$t('common.thirdOrderNo')" min-width="170"></el-table-column>
        <el-table-column prop="user_id" :label="$t('common.userId')" min-width="100"></el-table-column>
        <el-table-column prop="pkg" :label="$t('common.packageName')" min-width="150"></el-table-column>
        <el-table-column prop="amount" :label="$t('common.amount')" min-width="100"></el-table-column>
        <el-table-column prop="actual_amount" :label="$t('common.actualPaid')" min-width="110"></el-table-column>
        <el-table-column :label="$t('common.status')" min-width="110">
          <template slot-scope="scope">{{ statusText(scope.row.status) }}</template>
        </el-table-column>
        <el-table-column prop="fail_reason" :label="$t('common.failReason')" min-width="160"></el-table-column>
        <el-table-column prop="area_code" :label="$t('common.areaCode')" min-width="80"></el-table-column>
        <el-table-column prop="currency_code" :label="$t('common.currency')" min-width="80"></el-table-column>
        <el-table-column prop="created_at" :label="$t('common.submitTime')" min-width="160"></el-table-column>
        <el-table-column prop="updated_at" :label="$t('common.updatedAt')" min-width="160"></el-table-column>
      </el-table>
    </div>
    <el-pagination
      :total="count"
      :current-page.sync="search.page"
      :page-size="search.size"
      layout="total, prev, pager, next"
      @current-change="loadData"
    ></el-pagination>
  </section>
</template>

<script>
import { defaultDateRange, isDateRangeOverDays } from '../utils/date';

export default {
  name: 'Recharges',
  data() {
    const range = defaultDateRange();
    return {
      search: { page: 1, size: 20, created_at__gte: range[0], created_at__lt: range[1] },
      tableData: [],
      count: 0,
      rechargeAmounts: [],
      successfulRechargeAmounts: [],
      rechargeStatuses: [
        { value: 0, name: 'READY', labelKey: 'payRecordStatus.ready' },
        { value: 2, name: 'SUCCESS', labelKey: 'payRecordStatus.success' },
        { value: 4, name: 'FAIL', labelKey: 'payRecordStatus.failed' },
        { value: 116, name: 'UTR', labelKey: 'payRecordStatus.utr' },
        { value: 117, name: 'RISK1', labelKey: 'payRecordStatus.riskReview' }
      ]
    };
  },
  created() {
    this.loadData();
  },
  computed: {
    tableLanguage() {
      return this.$i18nState.language;
    }
  },
  methods: {
    reload() {
      this.search.page = 1;
      this.loadData();
    },
    validateDateRange() {
      if (!isDateRangeOverDays(this.search.created_at__gte, this.search.created_at__lt, 31)) return true;
      this.$message.error(this.$t('common.dateRangeTooLong'));
      return false;
    },
    buildQueryParams() {
      return Object.keys(this.search).reduce((params, key) => {
        const value = this.search[key];
        if (value !== '' && value !== null && value !== undefined) params[key] = value;
        return params;
      }, {});
    },
    statusText(status) {
      this.tableLanguage;
      const item = this.rechargeStatuses.find(one => String(one.value) === String(status) || one.name === status);
      return item ? this.$t(item.labelKey) : (status || '-');
    },
    async loadData() {
      if (!this.validateDateRange()) return;
      const resp = await this.$axios.get('/admin/exagent/console/recharges/', { params: this.buildQueryParams() });
      if (resp.data.status !== 0) return;
      const data = resp.data.data || {};
      this.tableData = data.list || [];
      this.count = data.count || 0;
      this.rechargeAmounts = data.recharge_amounts || [];
      this.successfulRechargeAmounts = data.successful_recharge_amounts || [];
    }
  }
};
</script>

<style lang="less" scoped>
.metric-list {
  margin-top: 8px;
}

.metric-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 26px;
  color: #20283a;

  span {
    color: #66788a;
    font-size: 13px;
  }

  strong {
    font-size: 18px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/Recharges.vue